import { useEffect, useState } from "react";
import { Search, Check } from "lucide-react";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import { TeamRepository } from "../infrastructure/repositories/TeamRepository";
import { UserConnectionStatus } from "../types/Team";
import { Badge } from "./ui/badge";
import { Swimmer } from "../types";

export function ChangeSwimmerContent() {
  const [swimmers, setSwimmers] = useState<Swimmer[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    const fetchSwimmers = async () => {
      const repo = new TeamRepository();
      try {
        const coachId = Number(localStorage.getItem("coach_id")) || 0;
        const status = UserConnectionStatus.Accepted;
        const response = await repo.getCoachSwimmersApi(coachId, status);
 
        const swimmersArray: Swimmer[] = (Array.isArray(response.data)
              ? response.data
              : JSON.parse(typeof response.data === "string" ? response.data : "[]")
            ).map((s: any) => ({
              ...s,
              coach_id: s.coach_id ?? s.CoachId ?? s.coachId ?? "",
            }));

        const userDataRaw = localStorage.getItem("user_data");
        const loggedInUser = userDataRaw ? JSON.parse(userDataRaw) : null;

      const fallbackPrimary =
        loggedInUser ??
        swimmersArray[0] ??
        null;

        if (!fallbackPrimary) {
          setSwimmers([]);
          return;
        }

        
        const allSwimmers = [
          fallbackPrimary,
          ...swimmersArray.filter((s) => s.id !== fallbackPrimary.id),
        ];

        const selectedSwimmerId =
            localStorage.getItem("selected_swimmer_id") || fallbackPrimary.id;

        const updatedSwimmers = allSwimmers.map((s) => ({
          ...s,
          role: s.id === selectedSwimmerId ? "Primary" : "Secondary",
        }));

        const selectedSwimmer = updatedSwimmers.find(
          (s) => s.id === selectedSwimmerId
        );
        if (selectedSwimmer) {
           localStorage.setItem("selected_swimmer_id", selectedSwimmer.id);
        
          localStorage.setItem(
            "selected_swimmer_name",
            `${selectedSwimmer.fname} ${selectedSwimmer.lname}`
          );
          localStorage.setItem(
            "selected_swimmer_data",
            JSON.stringify(selectedSwimmer)
          );
          localStorage.setItem(
            "selected_swimmer_pic",
            selectedSwimmer.image ?? ""
          );
        }

        setSwimmers(updatedSwimmers);
      } catch (err) {
        console.error("Error fetching swimmers:", err);
        setError("Failed to load swimmers");
      }
    };

    fetchSwimmers();
  }, []);

  const switchSwimmer = (swimmer: Swimmer) => {
    localStorage.setItem("selected_swimmer_id", swimmer.id);
    localStorage.setItem("selected_swimmer_name", swimmer.fname + " " + swimmer.lname);
    localStorage.setItem("selected_swimmer_data", JSON.stringify(swimmer));
    localStorage.setItem("selected_swimmer_pic", swimmer.image ?? "");
    console.log("coachid", swimmer.coach_id);
    localStorage.setItem("selected_swimmer_coach_id", swimmer.coach_id ?? "");

    // Update UI roles
    setSwimmers((prev) =>
      prev.map((s) => ({
        ...s,
        role: s.id === swimmer.id ? "Primary" : "Secondary",
      }))
    );
  };

  if (error) return <p className="text-red-500">{error}</p>;

  const filteredSwimmers = swimmers.filter((s) => {
    const fullName = `${s.fname} ${s.lname}`.toLowerCase();
    return (
      fullName.includes(searchTerm.toLowerCase()) ||
      s.email?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Manage Swimmers</h1>
          <p className="text-muted-foreground">
            Switch between different swimmer profiles
          </p>
        </div>
        {/* <Button>
          <Plus className="mr-2 h-4 w-4" />
          Add Swimmer
        </Button> */}
      </div>

      {/* Search */}
      <Card>
        <CardContent className="pt-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search swimmers by name or email..."
              className="pl-10"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </CardContent>
      </Card>

      {/* Swimmers List */}
      <div className="space-y-4">
        {filteredSwimmers.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center">
            No swimmers found
          </p>
        ) : (
          filteredSwimmers.map((swimmer) => (
            <Card key={swimmer.id} className="hover:shadow-md transition-shadow">
              <CardContent className="p-6 flex justify-between items-center">
                <div className="flex items-center space-x-4">
                  <Avatar className="h-12 w-12">
                    <AvatarImage src={swimmer.image || undefined} />
                    <AvatarFallback>
                      {`${swimmer.fname[0] || ""}${swimmer.lname[0] || ""}`}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <h3 className="font-semibold">
                      {`${swimmer.fname} ${swimmer.lname}`}
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      {swimmer.email}
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  {swimmer.role === "Primary" ? (
                    <Badge variant="outline" className="flex items-center">
                      <Check className="mr-1 h-3 w-3" />
                      Current
                    </Badge>
                  ) : (
                    <Button onClick={() => switchSwimmer(swimmer)}>
                      Switch To
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
}
